import { createServerFn } from "@tanstack/react-start";
import { getRequestIP } from "@tanstack/react-start/server";
import { contactMessageSchema, type ContactMessage } from "@/lib/contact-schema";
import { deliverContactEmails, generateContactId } from "./contact-email.server";

type SubmitSuccess = { ok: true; requestId: string };
type SubmitFailure = {
  ok: false;
  error: string;
  fieldErrors?: Record<string, string>;
};

export type SubmitContactResult = SubmitSuccess | SubmitFailure;

const rateWindowMs = 10 * 60 * 1000;
const maxPerWindow = 8;
const duplicateWindowMs = 60 * 1000;

const ipHits = new Map<string, number[]>();
const recentHashes = new Map<string, number>();

function clientIp(): string {
  try {
    return getRequestIP({ xForwardedFor: true }) || "unknown";
  } catch {
    return "unknown";
  }
}

function isRateLimited(ip: string): boolean {
  const now = Date.now();
  const next = (ipHits.get(ip) ?? []).filter((t) => now - t < rateWindowMs);
  if (next.length >= maxPerWindow) {
    ipHits.set(ip, next);
    return true;
  }
  next.push(now);
  ipHits.set(ip, next);
  return false;
}

function isDuplicate(data: ContactMessage): boolean {
  const now = Date.now();
  for (const [key, ts] of recentHashes) {
    if (now - ts > duplicateWindowMs) recentHashes.delete(key);
  }
  const key = [
    data.email.trim().toLowerCase(),
    data.subject,
    data.message.trim().slice(0, 80),
  ].join("|");
  const prev = recentHashes.get(key);
  if (prev && now - prev < duplicateWindowMs) return true;
  recentHashes.set(key, now);
  return false;
}

function flattenZodErrors(error: {
  flatten: () => { fieldErrors: Record<string, string[] | undefined>; formErrors: string[] };
}): Record<string, string> {
  const flat = error.flatten();
  const out: Record<string, string> = {};
  for (const [key, messages] of Object.entries(flat.fieldErrors)) {
    if (messages?.[0]) out[key] = messages[0];
  }
  return out;
}

export const submitContactMessage = createServerFn({ method: "POST" })
  .validator((input: unknown) => input)
  .handler(async ({ data: input }): Promise<SubmitContactResult> => {
    const ip = clientIp();
    if (isRateLimited(ip)) {
      return {
        ok: false,
        error: "Too many messages were submitted recently. Please wait a few minutes and try again.",
      };
    }

    const honeypot =
      input &&
      typeof input === "object" &&
      "companyWebsite" in input &&
      typeof (input as { companyWebsite?: unknown }).companyWebsite === "string"
        ? (input as { companyWebsite: string }).companyWebsite.trim()
        : "";
    if (honeypot) {
      return { ok: true, requestId: generateContactId() };
    }

    const parsed = contactMessageSchema.safeParse(input);
    if (!parsed.success) {
      return {
        ok: false,
        error: "Please review the highlighted fields and try again.",
        fieldErrors: flattenZodErrors(parsed.error),
      };
    }

    const data = parsed.data;
    if (isDuplicate(data)) {
      return {
        ok: false,
        error: "This message looks like a duplicate submission. Please wait a moment before trying again.",
      };
    }

    const requestId = generateContactId();
    const delivery = await deliverContactEmails(data, requestId);
    if (!delivery.ok) {
      return {
        ok: false,
        error:
          "We couldn't send your message. Your information has not been lost — please try again or contact Auburn RV Resort directly.",
      };
    }

    return { ok: true, requestId };
  });
